import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { LinkTo } from '@ember/routing'
import { array } from '@ember/helper'
import type Store from '@ember-data/store'
import { t } from 'ember-intl'
import { or, and, not } from 'ember-truth-helpers'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { trackedFunction } from 'reactiveweb/function'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { RolloverSubscriptionsTable } from 'teacher-ui-client/components/rollover/subscriptions-table'
import RolloverHistoryTable from 'teacher-ui-client/components/rollover/history-table'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { RolloverStatus } from 'teacher-ui-client/components/rollover/status'
import { RolloverState } from 'teacher-ui-client/school-rollover/manage-rollover-states'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type SchoolRollover from 'teacher-ui-client/models/school-rollover'
import ManageClassesThumb from '@images/rollover/manage-classes-thumb.png'
import ManageClassesImage from '@images/rollover/manage-classes.png'
import ManageStudentsThumb from '@images/rollover/manage-students-thumb.png'
import ManageStudentsImage from '@images/rollover/manage-students.png'
import ManageTeachersThumb from '@images/rollover/manage-teachers-thumb.png'
import ManageTeachersImage from '@images/rollover/manage-teachers.png'
import { ManageRolloverOneRosterRolloutPage } from 'teacher-ui-client/manage/rollover/wizard/template'
import canAccess from 'teacher-ui-client/helpers/can-access'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import type SubscriptionUtilisation from 'teacher-ui-client/models/subscription-utilisation'
import {
  InlineAlert,
  AlertType,
  InlineAlertSubtitle,
  InlineAlertTitle,
  InlineAlertActionsContainer,
} from 'teacher-ui-client/components/inline-alert'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface DisableLinkSignature {
  Element: HTMLAnchorElement
  Args: {
    route: string
    models?: string[]
    disabled?: boolean
    disabledTooltip?: string
  }
  Blocks: {
    default: []
  }
}

const DisableLink: TOC<DisableLinkSignature> = <template>
  <LinkTo
    class="[&.disabled]:text-dusty-black-100 inline-flex items-center gap-2 font-semibold"
    @route={{@route}}
    @models={{if @models @models (array)}}
    @disabled={{@disabled}}
    aria-disabled={{if @disabled "true" "false"}}
    ...attributes
  >
    {{yield}}
    <PhosphorIcon @icon="phosphor-arrow-circle-right-fill" class="h-5 w-5" />

    {{#if @disabled}}
      <Tooltip @text={{@disabledTooltip}} />
    {{/if}}
  </LinkTo>
</template>

interface CopyClassesAlertSignature {
  Args: {
    recentRollover: SchoolRollover
  }
}

const CopyClassesAlert: TOC<CopyClassesAlertSignature> = <template>
  <InlineAlert @type={{AlertType.Information}} data-test-copy-classes-alert>

    <InlineAlertTitle>
      {{t
        "manage.rollover.instructions.copyClassesAlert.recentlyCompleted"
        subscriptionType=(t @recentRollover.subscriptionType)
      }}
    </InlineAlertTitle>
    <InlineAlertSubtitle>

      <p>
        {{t "manage.rollover.instructions.copyClassesAlert.toCopy"}}
        <LinkTo @route="subscription-type.copy-classes">
          {{t "manage.rollover.instructions.copyClassesAlert.copyClasses"}}
        </LinkTo>.
      </p>
      <p>{{t "manage.rollover.instructions.copyClassesAlert.ifDifferent"}}</p>
    </InlineAlertSubtitle>
    <InlineAlertActionsContainer>
      <UiButtonLinkTo @route="subscription-type.copy-classes" @appearance={{ButtonAppearance.regular}}>
        {{t "manage.rollover.instructions.copyClassesAlert.copyClasses"}}
      </UiButtonLinkTo>
    </InlineAlertActionsContainer>

  </InlineAlert>
</template>

interface DoRolloverAlertSignature {
  Args: {
    showReadingRolloverLink?: boolean
    showMathsRolloverLink?: boolean
  }
}

const DoRolloverAlert: TOC<DoRolloverAlertSignature> = <template>
  <InlineAlert @type={{AlertType.Information}} data-test-do-rollover-alert>
    <InlineAlertTitle>
      <p>
        {{t
          "manage.rollover.instructions.doRolloverAlert.youMayNeedTo"
          htmlSafe=true
          reading=(t SubscriptionType.Reading)
          maths=(t SubscriptionType.Maths)
          writing=(t SubscriptionType.Writing)
        }}
      </p>
    </InlineAlertTitle>
    <InlineAlertSubtitle>
      {{#if (and @showReadingRolloverLink @showMathsRolloverLink)}}
        <p>
          {{t "manage.rollover.instructions.doRolloverAlert.youCanDoThis"}}
          <LinkTo @route="manage.rollover.wizard" @models={{array SubscriptionType.Reading}}>
            {{t "manage.rollover.instructions.doRolloverAlert.link" subscriptionType=(t SubscriptionType.Reading)}}
          </LinkTo>
          {{t "manage.rollover.instructions.doRolloverAlert.and"}}
          <LinkTo @route="manage.rollover.wizard" @models={{array SubscriptionType.Maths}}>
            {{t "manage.rollover.instructions.doRolloverAlert.link" subscriptionType=(t SubscriptionType.Maths)}}
          </LinkTo>
          {{t "manage.rollover.instructions.doRolloverAlert.pages"}}
        </p>
      {{else if @showReadingRolloverLink}}
        <p>
          {{t "manage.rollover.instructions.doRolloverAlert.youCanDoThis"}}
          <LinkTo @route="manage.rollover.wizard" @models={{array SubscriptionType.Reading}}>
            {{t "manage.rollover.instructions.doRolloverAlert.link" subscriptionType=(t SubscriptionType.Reading)}}
          </LinkTo>
          {{t "manage.rollover.instructions.doRolloverAlert.page"}}
        </p>
      {{else if @showMathsRolloverLink}}
        <p>
          {{t "manage.rollover.instructions.doRolloverAlert.youCanDoThis"}}
          <LinkTo @route="manage.rollover.wizard" @models={{array SubscriptionType.Maths}}>
            {{t "manage.rollover.instructions.doRolloverAlert.link" subscriptionType=(t SubscriptionType.Maths)}}
          </LinkTo>
          {{t "manage.rollover.instructions.doRolloverAlert.page"}}
        </p>
      {{/if}}
    </InlineAlertSubtitle>

  </InlineAlert>
</template>

interface ImageLinkSignature {
  Args: {
    thumbnailSrc: string
    imageSrc: string
    alt: string
  }
}

const ImageLink: TOC<ImageLinkSignature> = <template>
  <a
    class="border-dusty-black-100 inline-block rounded-sm border"
    href={{@imageSrc}}
    target="_blank"
    rel="noopener noreferrer"
  >
    <img width="320" alt={{@alt}} src={{@thumbnailSrc}} />
  </a>
</template>

export class ManageRolloverInstructionsWrapperPage extends Component<unknown> {
  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  get schoolName() {
    return this.session.currentSchool.name
  }

  <template>
    <PageHeader>
      <:title>
        <h1>{{t "manage.rollover.title"}}</h1>
      </:title>
    </PageHeader>

    {{#if (canAccess "one_roster")}}
      <ManageRolloverOneRosterRolloutPage
        @subscriptionType={{this.subscriptionType.current}}
        @schoolCountryCode={{this.session.currentSchool.countryCode}}
        @schoolName={{this.schoolName}}
      />
    {{else}}
      <RolloverInstructionsPage />
    {{/if}}
  </template>
}

export class RolloverInstructionsPage extends Component<unknown> {
  @service schoolRollover!: SchoolRolloverService

  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @service session!: SessionService

  @service intl!: IntlService

  fetchRollovers = trackedFunction(this, async () => {
    await Promise.resolve() // disconnect tracking before store calls
    void this.schoolRollover.fetchRollovers()
  })

  get fetchingRollovers() {
    return this.fetchRollovers.isPending
  }

  get hasActiveRollover() {
    return this.schoolRollover.activeRollovers.length > 0
  }

  get disableManagementLinks() {
    return this.fetchingRollovers || this.hasActiveRollover
  }

  get isReadingSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  get isMathsSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Maths)
  }

  get hasReadingUtilisation() {
    return this.store
      .peekAll<SubscriptionUtilisation>('subscription-utilisation')
      .some((util) => util.subscriptionType === SubscriptionType.Reading)
  }

  get hasMathsUtilisation() {
    return this.store
      .peekAll<SubscriptionUtilisation>('subscription-utilisation')
      .some((util) => util.subscriptionType === SubscriptionType.Maths)
  }

  /**
   * Only want to grey out step 2 if _both_ links are disabled due to not being a reading/maths subco or "writing-only subscription"
   * This means we don't want it greyed out if links are disabled for some other reason (like rollover in progress ).
   */
  get greyStep2() {
    return !this.disableManagementLinks && this.readingRolloverLink.disabled && this.mathsRolloverLink.disabled
  }

  get readingRolloverLink() {
    const subscriptionType = this.intl.t(SubscriptionType.Reading)
    if (!this.isReadingSubco) {
      return {
        disabled: true,
        tooltip: this.intl.t('manage.rollover.instructions.disabledTooltips.notSubcoFor', { subscriptionType }),
      }
    }
    if (!this.hasReadingUtilisation) {
      return {
        disabled: true,
        tooltip: this.intl.t('manage.rollover.instructions.disabledTooltips.noSub', { subscriptionType }),
      }
    }
    return { disabled: false }
  }

  get mathsRolloverLink() {
    const subscriptionType = this.intl.t(SubscriptionType.Maths)
    if (!this.isMathsSubco) {
      return {
        disabled: true,
        tooltip: this.intl.t('manage.rollover.instructions.disabledTooltips.notSubcoFor', { subscriptionType }),
      }
    }
    if (!this.hasMathsUtilisation) {
      return {
        disabled: true,
        tooltip: this.intl.t('manage.rollover.instructions.disabledTooltips.noSub', { subscriptionType }),
      }
    }
    return { disabled: false }
  }

  get schoolSubscriptions() {
    return this.store
      .peekAll<SchoolSubscription>('school-subscription')
      .filter((schoolSub) => schoolSub.subscriptionType === this.subscriptionType.current && !schoolSub.isExpired)
  }

  <template>
    {{#if this.hasActiveRollover}}
      <RolloverStatus @status={{RolloverState.Locked}} />
    {{else if this.schoolRollover.latestRecentRollover}}
      <CopyClassesAlert @recentRollover={{this.schoolRollover.latestRecentRollover}} />
    {{else}}
      <DoRolloverAlert
        @showReadingRolloverLink={{not this.readingRolloverLink.disabled}}
        @showMathsRolloverLink={{not this.mathsRolloverLink.disabled}}
      />
    {{/if}}

    <h3 class="my-8">
      {{t "manage.rollover.instructions.howToTitle" subscriptionType=(t this.subscriptionType.current)}}
    </h3>

    <div class="grid grid-cols-2 gap-8">
      <div data-test-step-block="1">
        <div class="text-dusty-black-250 text-sm font-bold">{{t "manage.rollover.instructions.step1.title"}}</div>
        <strong class="text-base">
          {{t "manage.rollover.instructions.step1.checkCapacity" subscriptionType=(t this.subscriptionType.current)}}
        </strong>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step1.addCapacity"}}</p>
        <DisableLink
          @route="manage.subscription"
          @disabled={{this.disableManagementLinks}}
          @disabledTooltip={{t "manage.rollover.instructions.disabledTooltips.rolloverInProgress"}}
        >
          {{t "manage.title.subscription"}}
        </DisableLink>
      </div>
      <div class="space-y-3">
        <span class="text-base font-semibold">{{t "manage.rollover.instructions.step1.activeSubscriptions"}}</span>
        <RolloverSubscriptionsTable class="w-full" @schoolSubscriptions={{this.schoolSubscriptions}} />
      </div>
      <hr class="border-dusty-black-50 col-span-2 border-t-2" />
      <div data-test-step-block="2">
        <div class={{if this.greyStep2 "opacity-50"}}>
          <div class="text-dusty-black-250 text-sm font-bold">{{t "manage.rollover.instructions.step2.title"}}</div>
          <strong class="text-base">
            {{t "manage.rollover.instructions.step2.checkRolloverHistory" reading=(t "reading") maths=(t "maths")}}
          </strong>
          <p class="mb-3 text-base">
            {{t "manage.rollover.instructions.step2.doRollover" reading=(t "reading") maths=(t "maths")}}
          </p>
        </div>
        <DisableLink
          data-test-rollover-link="reading"
          class="mr-8"
          @route="manage.rollover.wizard"
          @models={{array "reading"}}
          @disabled={{or this.disableManagementLinks this.readingRolloverLink.disabled}}
          @disabledTooltip={{if
            this.disableManagementLinks
            (t "manage.rollover.instructions.disabledTooltips.rolloverInProgress")
            this.readingRolloverLink.tooltip
          }}
        >
          {{t "manage.rollover.instructions.step2.manageRollover" subscriptionType=(t "reading")}}
        </DisableLink>
        <DisableLink
          data-test-rollover-link="maths"
          class="mr-8"
          @route="manage.rollover.wizard"
          @models={{array "maths"}}
          @disabled={{or this.disableManagementLinks this.mathsRolloverLink.disabled}}
          @disabledTooltip={{if
            this.disableManagementLinks
            (t "manage.rollover.instructions.disabledTooltips.rolloverInProgress")
            this.mathsRolloverLink.tooltip
          }}
        >
          {{t "manage.rollover.instructions.step2.manageRollover" subscriptionType=(t "maths")}}
        </DisableLink>
      </div>
      <div>
        <span class="text-base font-semibold">{{t "manage.rollover.instructions.step2.rolloverHistory"}}</span>
        <div>
          {{#if this.fetchRollovers.isPending}}
            <FidgetSpinnerWaveComponent @centered={{true}} @small={{true}} />
          {{else if this.schoolRollover.completedRollovers}}
            <RolloverHistoryTable class="w-full" @rollovers={{this.schoolRollover.completedRollovers}} />
          {{else}}
            <p data-test-no-rollovers class="mb-3 text-base italic">
              {{t "manage.rollover.instructions.step2.emptyRolloverHistory"}}
            </p>
          {{/if}}
        </div>
      </div>
      <hr class="border-dusty-black-50 col-span-2 border-t-2" />
      <div data-test-step-block="3">
        <div class="text-dusty-black-250 text-sm font-bold">{{t "manage.rollover.instructions.step3.title"}}</div>
        <strong class="text-base">
          {{t "manage.rollover.instructions.step3.manageYourTeachers"}}
        </strong>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step3.addTeachers"}}</p>
        <DisableLink
          @route="manage.teachers"
          @disabled={{this.disableManagementLinks}}
          @disabledTooltip={{t "manage.rollover.instructions.disabledTooltips.rolloverInProgress"}}
        >
          {{t "manage.title.teachers"}}
        </DisableLink>
      </div>
      <div>
        <ImageLink
          @thumbnailSrc={{ManageTeachersThumb}}
          @imageSrc={{ManageTeachersImage}}
          @alt={{t "manage.rollover.instructions.step3.imageAlt"}}
        />
      </div>
      <hr class="border-dusty-black-50 col-span-2 border-t-2" />
      <div data-test-step-block="4">
        <div class="text-dusty-black-250 text-sm font-bold">{{t "manage.rollover.instructions.step4.title"}}</div>
        <strong class="text-base">
          {{t "manage.rollover.instructions.step4.removeOldClasses"}}
        </strong>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step4.newClasses"}}</p>
        <p class="mb-3 text-base">
          {{t "manage.rollover.instructions.step4.copyingClasses" reading=(t "reading") maths=(t "maths")}}
        </p>
        <DisableLink
          @route="manage.classes"
          @disabled={{this.disableManagementLinks}}
          @disabledTooltip={{t "manage.rollover.instructions.disabledTooltips.rolloverInProgress"}}
        >
          {{t "manage.title.classes"}}
        </DisableLink>
      </div>
      <div>
        <ImageLink
          @thumbnailSrc={{ManageClassesThumb}}
          @imageSrc={{ManageClassesImage}}
          @alt={{t "manage.rollover.instructions.step4.imageAlt"}}
        />
      </div>
      <hr class="border-dusty-black-50 col-span-2 border-t-2" />
      <div data-test-step-block="5">
        <div class="text-dusty-black-250 text-sm font-bold">{{t "manage.rollover.instructions.step5.title"}}</div>
        <strong class="text-base">
          {{t "manage.rollover.instructions.step5.manageYourStudents"}}
        </strong>
        <p class="mb-3 text-base">
          {{t "manage.rollover.instructions.step5.copyingClasses" reading=(t "reading") maths=(t "maths")}}
        </p>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step5.rolloverExisting"}}</p>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step5.addNewStudents"}}</p>
        <p class="mb-3 text-base">{{t "manage.rollover.instructions.step5.putIntoClasses"}}</p>
        <DisableLink
          @route="manage.students"
          @disabled={{this.disableManagementLinks}}
          @disabledTooltip={{t "manage.rollover.instructions.disabledTooltips.rolloverInProgress"}}
        >
          {{t "manage.title.students"}}
        </DisableLink>
      </div>
      <div>
        <ImageLink
          @thumbnailSrc={{ManageStudentsThumb}}
          @imageSrc={{ManageStudentsImage}}
          @alt={{t "manage.rollover.instructions.step5.imageAlt"}}
        />
      </div>
      <hr class="border-dusty-black-50 col-span-2 border-t-2" />
    </div>
  </template>
}

export default RouteTemplate(ManageRolloverInstructionsWrapperPage)
